/**
 * PATH: packages/email-hub/src/feature/pages/inbox-page/controller/useReaderRouteSelection.ts
 * FILE_ID: EMAIL-INBOX-READER-ROUTE-001
 * PURPOSE: Own URL-backed reader selection and list-return history semantics.
 */

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  readOpenEmailId,
  withOpenEmailId,
} from '../../../email-reader/navigation'

const READER_HISTORY_STATE_KEY = 'emailReaderListEntry'

export function useReaderRouteSelection() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const routeEmailId = readOpenEmailId(searchParams)
  const [selectedEmailId, setSelectedEmailIdState] =
    useState<string | null>(routeEmailId)
  const selectedEmailIdRef = useRef(selectedEmailId)

  useEffect(() => {
    if (routeEmailId === selectedEmailIdRef.current) return
    selectedEmailIdRef.current = routeEmailId
    setSelectedEmailIdState(routeEmailId)
  }, [routeEmailId])

  const writeSelectedEmailId = useCallback(
    (emailId: string | null, replace: boolean) => {
      selectedEmailIdRef.current = emailId
      setSelectedEmailIdState(emailId)
      if (readOpenEmailId(searchParams) === emailId) return
      setSearchParams(
        (current) => withOpenEmailId(current, emailId),
        {
          replace,
          state: replace
            ? location.state
            : {
                ...(typeof location.state === 'object'
                  && location.state !== null
                  ? location.state
                  : {}),
                [READER_HISTORY_STATE_KEY]: true,
              },
        },
      )
    },
    [location.state, searchParams, setSearchParams],
  )

  const setSelectedEmailId: Dispatch<SetStateAction<string | null>> =
    useCallback(
      (next) => {
        const resolved =
          typeof next === 'function'
            ? next(selectedEmailIdRef.current)
            : next
        writeSelectedEmailId(resolved, true)
      },
      [writeSelectedEmailId],
    )
  const openSelectedEmailId = useCallback(
    (emailId: string) => writeSelectedEmailId(emailId, false),
    [writeSelectedEmailId],
  )
  const closeSelectedEmail = useCallback(() => {
    const readerWasPushed =
      typeof location.state === 'object'
      && location.state !== null
      && READER_HISTORY_STATE_KEY in location.state
    if (readerWasPushed) {
      navigate(-1)
      return
    }
    setSelectedEmailId(null)
  }, [location.state, navigate, setSelectedEmailId])

  return {
    selectedEmailId,
    setSelectedEmailId,
    openSelectedEmailId,
    closeSelectedEmail,
  }
}
